import React, { useState, useEffect } from "react";
import { Card, Checkbox } from "@material-tailwind/react";
import toast from "react-hot-toast";
import {
  formatDateForDisplay,
  formatDateForPopUp,
} from "../../../../utils/Common";

export default function ExperiencePopUp({
  experience,
  closeModal,
  handleSaveExperience,
  isAddMode,
}) {
  const [formData, setFormData] = useState({
    title: "",
    company: "",
    location: "",
    start_date: "",
    end_date: "",
    currently_working: false,
    description: "",
  });

  useEffect(() => {
    if (experience) {
      setFormData({
        title: experience.title || "",
        company: experience.company || "",
        location: experience.location || "",
        start_date:
          formatDateForPopUp(experience.start_date)?.split("T")[0] || "",
        end_date: experience.currently_working
          ? ""
          : formatDateForPopUp(experience.end_date)?.split("T")[0] || "",
        currently_working: experience.currently_working || false,
        description: experience.description || "",
      });
    }
  }, [experience]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear end date when "currently working" is checked
    if (name === "currently_working" && checked) {
      setFormData((prev) => ({
        ...prev,
        currently_working: true,
        end_date: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.company || !formData.start_date) {
      toast.error("Title, company, and start date are required");
      return;
    }

    if (!formData.currently_working && !formData.end_date) {
      toast.error("Please provide an end date or mark as current position");
      return;
    }

    if (
      formData.start_date > formData.end_date &&
      !formData.currently_working
    ) {
      toast.error("End date cannot be before start date");
      return;
    }

    await handleSaveExperience({
      ...formData,
      end_date: formData.currently_working ? null : formData.end_date,
    });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-80 z-50">
      <Card
        color="transparent"
        shadow={false}
        className="w-[90%] max-w-[700px] max-h-[90vh] bg-gray-900 rounded-[20px] overflow-hidden flex flex-col"
      >
        <div className="overflow-y-auto flex-1 p-6">
          <form
            onSubmit={handleSubmit}
            className="text-white flex flex-col space-y-6"
          >
            <h2 className="text-xl font-bold mb-4">
              {isAddMode ? "Add Work Experience" : "Edit Work Experience"}
            </h2>

            {/* Job Title */}
            <div className="relative z-0 w-full group">
              <label htmlFor="title" className="block text-sm">
                Job Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                className="block py-3 px-4 w-full text-sm bg-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                onChange={handleChange}
                value={formData.title}
              />
            </div>

            {/* Company */}
            <div className="relative z-0 w-full group">
              <label htmlFor="company" className="block text-sm">
                Company <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company"
                className="block py-3 px-4 w-full text-sm bg-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                onChange={handleChange}
                value={formData.company}
              />
            </div>

            {/* Location */}
            <div className="relative z-0 w-full group">
              <label htmlFor="location" className="block text-sm">
                Location
              </label>
              <input
                type="text"
                name="location"
                className="block py-3 px-4 w-full text-sm bg-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={handleChange}
                value={formData.location}
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Start Date */}
              <div className="relative z-0 w-full group">
                <label htmlFor="start_date" className="block text-sm">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="start_date"
                  className="block py-3 px-4 w-full text-sm bg-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  onChange={handleChange}
                  value={formData.start_date}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>

              {/* End Date */}
              <div className="relative z-0 w-full group">
                <label htmlFor="end_date" className="block text-sm">
                  End Date{" "}
                  {!formData.currently_working && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <input
                  type="date"
                  name="end_date"
                  className={`block py-3 px-4 w-full text-sm bg-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.currently_working ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={formData.currently_working}
                  onChange={handleChange}
                  value={formData.end_date}
                  min={formData.start_date}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>

            {/* Currently Working */}
            <div className="flex items-center">
              <Checkbox
                name="currently_working"
                checked={formData.currently_working}
                onChange={handleChange}
                color="blue"
                className="h-5 w-5 rounded"
              />
              <label htmlFor="currently_working" className="ml-2 text-sm">
                I am currently in this role
              </label>
            </div>

            {/* Description */}
            <div className="relative z-0 w-full group">
              <label htmlFor="description" className="block text-sm">
                Description
              </label>
              <textarea
                name="description"
                rows="4"
                className="block py-3 px-4 w-full text-sm bg-gray-800 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={handleChange}
                value={formData.description}
              />
            </div>

            <p className="text-sm text-gray-400">
              <span className="text-red-500">*</span> compulsory fields
            </p>

            <div className="flex items-center justify-between mt-5 space-x-4">
              <button
                type="submit"
                className="w-full px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                {isAddMode ? "Add Experience" : "Update Experience"}
              </button>
              <button
                type="button"
                onClick={closeModal}
                className="w-full px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
